import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

interface AvulsoCompareModalProps {
  serviceName: string;
  serviceSlug: string;
  avulsoPrice: number;
  avulsoDesc?: string;
  monthlyProd: { price_brl: number } | undefined | null;
  annualProd: { price_brl: number } | undefined | null;
  onClose: () => void;
}

export function AvulsoCompareModal({
  serviceName,
  avulsoPrice,
  avulsoDesc,
  monthlyProd,
  annualProd,
  onClose,
}: AvulsoCompareModalProps) {
  const annualMonthly = annualProd ? annualProd.price_brl / 12 : 0;
  const savingsPct = monthlyProd && annualProd
    ? Math.round((1 - annualMonthly / monthlyProd.price_brl) * 100)
    : 0;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ maxWidth: '640px', width: '100%', padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h2 className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', margin: 0 }}>
              Compare suas opções
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.25rem 0 0' }}>
              Para acessar {serviceName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.25rem', lineHeight: 1, padding: '0.25rem' }}
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem' }}>
          {/* Avulso option */}
          <div style={{ padding: '1rem', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)', display: 'flex', flexDirection: 'column' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.4rem' }}>Compra única</p>
            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>
              R${avulsoPrice.toFixed(2).replace('.', ',')}
            </p>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', flex: 1, margin: '0 0 0.75rem' }}>
              {avulsoDesc ?? `Acesso a ${serviceName}, apenas esta análise`}
            </p>
            <Link to="/pricing" className="btn btn-secondary" style={{ textAlign: 'center', fontSize: 'var(--text-caption)' }}>
              Comprar avulso
            </Link>
          </div>

          {/* Monthly plan option */}
          {monthlyProd && (
            <div style={{ padding: '1rem', borderRadius: 'var(--radius-card)', border: '1px solid rgba(74,222,128,0.3)', background: 'rgba(74,222,128,0.04)', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: '#4ade80', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.4rem' }}>Plano Mensal</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#4ade80', margin: '0 0 0.5rem' }}>
                R${monthlyProd.price_brl.toFixed(2).replace('.', ',')}<span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-muted)' }}>/mês</span>
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {[`${serviceName} incluído`, 'Mapa Natal + Previsões', 'Sinastria · AstroMatch', 'Tarot ilimitado'].map(f => (
                  <li key={f} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', display: 'flex', gap: '0.35rem' }}>
                    <Check size={12} style={{ color: '#4ade80', flexShrink: 0, marginTop: '2px' }} />{f}
                  </li>
                ))}
              </ul>
              <Link to="/pricing" className="btn btn-secondary" style={{ textAlign: 'center', fontSize: 'var(--text-caption)', borderColor: 'rgba(74,222,128,0.4)', color: '#4ade80' }}>
                Assinar Mensal
              </Link>
            </div>
          )}

          {/* Annual plan option */}
          {annualProd && (
            <div style={{ padding: '1rem', borderRadius: 'var(--radius-card)', border: '2px solid var(--petrol)', background: 'rgba(31,78,95,0.06)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: 'var(--petrol)', color: '#000', fontSize: '10px', fontWeight: 700, padding: '1px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                MELHOR VALOR
              </div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.4rem' }}>Plano Anual</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--petrol)', margin: '0 0 0.25rem' }}>
                R${annualMonthly.toFixed(2).replace('.', ',')}<span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-muted)' }}>/mês</span>
              </p>
              {savingsPct > 0 && (
                <p style={{ fontSize: '0.7rem', color: '#4ade80', fontWeight: 700, margin: '0 0 0.5rem' }}>Economize {savingsPct}% vs mensal</p>
              )}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {[`${serviceName} incluído`, 'Tudo do Plano Mensal', 'Sinastria ilimitada', 'Astro Coach · Previsão Anual'].map(f => (
                  <li key={f} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', display: 'flex', gap: '0.35rem' }}>
                    <Check size={12} style={{ color: 'var(--petrol)', flexShrink: 0, marginTop: '2px' }} />{f}
                  </li>
                ))}
              </ul>
              <Link to="/pricing" className="btn btn-primary btn-sm" style={{ textAlign: 'center', fontSize: 'var(--text-caption)' }}>
                ✦ Assinar Anual
              </Link>
            </div>
          )}
        </div>

        {/* Upgrade nudge */}
        <p style={{ textAlign: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '1rem' }}>
          💡 Com um plano, este e dezenas de outros serviços ficam disponíveis por uma fração do preço avulso.
        </p>
      </div>
    </div>
  );
}
